import React, {useEffect} from "react";
import {makeStyles} from "@material-ui/core/styles";
import AppBar from "@material-ui/core/AppBar";
import {ErrorScreen, HeaderToolbar, Loading} from "../../components";
import Toolbar from "@material-ui/core/Toolbar";
import {useAuthUser} from "../../components/auth-provider";
import {useStore} from "../../store";
import {fetchAccount} from "../../store/actions";
import {Link, Redirect} from "react-router-dom";
import Grid from "@material-ui/core/Grid";
import clsx from "clsx";
import ausPhone from "../../utils/aus-phone";
import Button from "@material-ui/core/Button";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import FuseAnimate from "../../components/fuse-animate/fuse-animate";
import Card from "@material-ui/core/Card";
import Typography from "@material-ui/core/Typography";
import callCredits from "../../utils/call-credits";

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
    },
    appBar: {
        zIndex: theme.zIndex.drawer + 1,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        marginTop: 16,
        justifyContent: "center",
    },
    card: {
        display: 'flex',
        flexDirection: 'column',
        padding: 24,
        width: '100%',
        maxWidth: 340,
        [theme.breakpoints.up('sm')]: {
            maxWidth: 360,
        },
    },
    listItem: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        marginBottom: theme.spacing(2),
        borderRadius: 3,
        maxWidth: 400,
    },
    number: {
        fontSize: 18,
        fontWeight: 700,
        '&.inActive': {
            textDecoration: 'line-through',
        },
    },
}));

//
// If the account has only 1 service, the recharge page redirects
// to the service recharge page, /recharge/<serviceId>.
//
// If the account has no services, it displays a prompt with a link
// to add a service /add-service
//
// If there is more than one service, a pick list is displayed to
// allow the user to choose which service to recharge.
//
const RechargePage = ({location}) => {
    const classes = useStyles();
    const [store, dispatch] = useStore();
    const {user} = useAuthUser();           // Get logged in user

    // Make sure the account is available in the store
    useEffect(()=> {
        dispatch(fetchAccount(user.accountId, store, dispatch))
    }, [user.accountId, store, dispatch])

    return (
        <div id="recharge" className={classes.root}>
            <AppBar position="fixed" className={classes.appBar}>
                <HeaderToolbar location={location.pathname}/>
            </AppBar>
            <Toolbar/>

            <main className={classes.content}>
                { store.account.isFetching ? <Loading/> : null }
                { store.account.error && <ErrorScreen error={store.account.error}/> }
                { store.account.error === null && !store.account.isFetching ?
                    <FuseAnimate animation="transition.expandIn" delay={100}>
                        <div id="recharge-list" className="flex flex-grow flex-col items-center">
                            <RechargeSelectList account={store.account}/>
                        </div>
                    </FuseAnimate>
                    : null
                }
            </main>
        </div>
    );

}

const RechargeSelectList = ({account}) => {
    const classes = useStyles();

    // Only show active services
    let services = [];
    if (account.services) {
        services = account.services.filter((service) => {
            return service.active;
        })
    }

    // Test stuff...
    // services = [
    //     {id:'101000000001', number: '+61398765432', name:'PhoneThru', meta:{zone:'Melbourne'}, callCredits: 236, callCreditsExpiry:'2021-06-19 23:59:59'},
    //     {id:'101000000002', number: '+61354621789', name:'PhoneThru', meta:{zone:'Castlemaine'}, callCredits: 123, callCreditsExpiry:'2021-06-18 23:59:59'}
    // ]
    //
    // services = [];

    // Redirect if there's only one number
    if (services.length === 1) {
        return <Redirect to={`/recharge/${services[0].id}`}/>
    }

    if (services.length === 0) {
        if (account.status < 1) {
            // The account is closed
            return (
                <Card className={classes.card}>
                    <Typography variant="h4" color="secondary" className="pb-6">Recharge</Typography>
                    <Typography variant="body1" >Your account is currently closed but you can reactivate it.</Typography>
                    <div className="flex flex-grow flex-row mt-6">
                        <Button
                            component={Link}
                            to={'/reactivate'}
                            variant="outlined"
                            className="w-full"
                            color="secondary"
                        >
                            Reactivate my account
                        </Button>
                    </div>
                </Card>
            )

        } else {
            // There's zero services...
            return (
                <Card className={classes.card}>
                    <Typography variant="h4" color="secondary" className="pb-6">Recharge</Typography>
                    <Typography variant="body1" >Hmm, we couldn't find any numbers you can recharge, but you can add one!</Typography>
                    <div className="flex flex-grow flex-row mt-6">
                        <Button
                            component={Link}
                            to={'/add-service'}
                            variant="outlined"
                            className="w-full"
                            color="secondary"
                        >
                            Add a Number
                        </Button>
                    </div>
                </Card>
            )
        }

    } else {
        // There's > 1 service...
        return (
            <Card className={classes.card}>
                <Typography variant="h6" className="pb-4">Choose a number to recharge</Typography>
                <List component="nav" className="">

        { services.map((service, index) => (
            <ListItem
                component={Link}
                to={`/recharge/${service.id}`}
                key={service.id}
                className={classes.listItem}
            >
                        <Grid container direction="column" >
                            <div>{service.name}</div>
                            <div className={clsx(classes.number, {
                                'inActive': service.active === 0})
                            }>
                                {ausPhone(service.number)}
                            </div>
                            { service.meta.zone !=='' ? <div className="mb-1">{service.meta.zone}</div>:null}
                            <Grid item container alignItems="center" className={classes.forwarding}>
                                <Credit service={service}/>
                            </Grid>
                        </Grid>
            </ListItem>
        ))}
            </List>
                </Card>
        )
    }
}

const Credit = ({service}) => {
    const {minutes, daysLeft} = callCredits(service);

    if (minutes > 0) {
        return (
            <>
                {/*<TimerOutlinedIcon fontSize="small" className="mr-1"/>*/}
                <div>{minutes} min ({daysLeft} {daysLeft === 1 ? 'day':'days'}) left</div>
            </>
        )
    } else {
        return (
            <>
                {/*<ReportProblemOutlinedIcon fontSize="small"  className="mr-1"/>*/}
                <div>No minutes left</div>
            </>
        )
    }
}


export default RechargePage;
